import { mockTransport } from './mock'
import type {
  BridgeEvent,
  BridgeEvents,
  BridgeMethod,
  BridgeReq,
  BridgeRes,
  BridgeResponse,
  Capabilities,
} from './types'

declare global {
  interface Window {
    /** Injected by flutter_inappwebview. */
    flutter_inappwebview?: { callHandler: (name: string, ...args: unknown[]) => Promise<unknown> }
    /** Called by native via evaluateJavascript to push events into the web app. */
    __yoloBridge?: { emit: (event: string, payload?: unknown) => void }
  }
}

export const BRIDGE_VERSION = 1
const HANDLER_NAME = 'yolo'
/** Native shell appends `YoloApp/<version>` to the user agent (applicationNameForUserAgent). */
const APP_UA = /\bYoloApp\/[\w.]+/
const DEFAULT_TIMEOUT_MS = 15_000
/** Methods waiting on the user (camera, permission prompts) get longer or no timeout. */
const TIMEOUTS: Partial<Record<BridgeMethod, number | null>> = {
  pickImage: null,
  getLocation: 30_000,
}

export class BridgeError extends Error {
  readonly code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'BridgeError'
    this.code = code
  }
}

export const isInApp = () => APP_UA.test(navigator.userAgent)
const mockEnabled = () => import.meta.env.VITE_BRIDGE_MOCK === 'true'

// ---------- events (native -> web) ----------

const listeners = new Map<string, Set<(payload: unknown) => void>>()

function emit(event: string, payload?: unknown) {
  listeners.get(event)?.forEach((fn) => {
    try {
      fn(payload)
    } catch (e) {
      console.error(`[bridge] listener for "${event}" failed`, e)
    }
  })
}

window.__yoloBridge = { emit }

export function onBridgeEvent<E extends BridgeEvent>(
  event: E,
  fn: (payload: BridgeEvents[E]) => void,
): () => void {
  const set = listeners.get(event) ?? new Set()
  set.add(fn as (payload: unknown) => void)
  listeners.set(event, set)
  return () => set.delete(fn as (payload: unknown) => void)
}

/** Dev/test helper to simulate a native event. */
export const emitBridgeEvent = <E extends BridgeEvent>(event: E, payload?: BridgeEvents[E]) =>
  emit(event, payload)

// ---------- calls (web -> native) ----------

type Transport = (method: string, payload: unknown) => Promise<unknown>

let platformReady: Promise<void> | null = null

/** flutter_inappwebview's callHandler is only safe after this event on Android. */
function waitForPlatform(): Promise<void> {
  if (typeof window.flutter_inappwebview?.callHandler === 'function') return Promise.resolve()
  platformReady ??= new Promise((resolve) => {
    window.addEventListener('flutterInAppWebViewPlatformReady', () => resolve(), { once: true })
  })
  return platformReady
}

async function getTransport(): Promise<Transport> {
  if (isInApp()) {
    await waitForPlatform()
    return (method, payload) =>
      window.flutter_inappwebview!.callHandler(HANDLER_NAME, { v: BRIDGE_VERSION, method, payload })
  }
  if (mockEnabled()) return mockTransport
  throw new BridgeError('NO_HOST', 'Not running inside the Yolo app')
}

function withTimeout<T>(promise: Promise<T>, ms: number | null, method: string): Promise<T> {
  if (ms === null) return promise
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new BridgeError('TIMEOUT', `${method} timed out`)), ms)
    promise.then(
      (v) => (clearTimeout(t), resolve(v)),
      (e) => (clearTimeout(t), reject(e)),
    )
  })
}

type Args<M extends BridgeMethod> = BridgeReq<M> extends void ? [] : [payload: BridgeReq<M>]

export async function callNative<M extends BridgeMethod>(
  method: M,
  ...args: Args<M>
): Promise<BridgeRes<M>> {
  const transport = await getTransport()
  const timeout = method in TIMEOUTS ? TIMEOUTS[method]! : DEFAULT_TIMEOUT_MS
  const res = (await withTimeout(transport(method, args[0] ?? null), timeout, method)) as
    BridgeResponse<BridgeRes<M>> | undefined

  if (!res || typeof res !== 'object' || !('ok' in res)) {
    throw new BridgeError('BAD_RESPONSE', `${method} returned an invalid envelope`)
  }
  if (!res.ok) throw new BridgeError(res.error.code, res.error.message)
  return res.data
}

// ---------- capabilities ----------

let capabilities: Promise<Capabilities> | null = null

export function getCapabilities(): Promise<Capabilities> {
  capabilities ??= callNative('getCapabilities').catch((e) => {
    capabilities = null
    throw e
  })
  return capabilities
}

/** Guard calls added in newer bridge versions so old app builds degrade gracefully. */
export async function canUse(method: BridgeMethod): Promise<boolean> {
  try {
    return (await getCapabilities()).methods.includes(method)
  } catch {
    return false
  }
}
